import React, { useState, useEffect } from 'react';
import NameList from './NameList';
import { FaQuestion } from 'react-icons/fa';
import { Tooltip } from 'react-tooltip';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CLIENT_ID = process.env.REACT_APP_TWITCH_CLIENT_ID;

const SyncFollowers = ({
  isAuthenticated,
  user,
  sendUsersToApp,
  electedUser,
  isSoundOn,
  volume,
  setVolume
}) => {
  const [followers, setFollowers] = useState([]);
  const [chatters, setChatters] = useState([]);
  const [streamLive, setStreamLive] = useState(false);
  const [recentChats, setRecentChats] = useState([]);
  const [usersSent, setUsersSent] = useState(false);
  const [syncButtonDisabled, setSyncButtonDisabled] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSyncButtonDisabled(!isAuthenticated || !user?.accessToken || !user?.id || !user?.name);
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (!usersSent && followers.length > 0) {
      sendUsersToApp(followers);
      setUsersSent(true);
    }
  }, [followers, sendUsersToApp, usersSent]);

  // Fetch stream info (live/offline)
  const fetchStreamInfo = async (accessToken, userId) => {
    try {
      const url = `https://api.twitch.tv/helix/streams?user_id=${userId}`;
      const res = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Client-ID': CLIENT_ID,
        },
      });
      if (res.data.data && res.data.data.length > 0) {
        return res.data.data[0]; // Stream is live
      }
      return null; // Stream is offline
    } catch (error) {
      console.error('Error fetching stream info:', error);
      return null;
    }
  };

  // Fetch all followers (pagination)
  const fetchAllFollowers = async (accessToken, broadcasterId) => {
    let allFollowers = [];
    let cursor = null;
    let keepGoing = true;
    while (keepGoing) {
      try {
        const url = `https://api.twitch.tv/helix/channels/followers?broadcaster_id=${broadcasterId}&first=100${cursor ? `&after=${cursor}` : ''}`;
        const res = await axios.get(url, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Client-ID': CLIENT_ID,
          },
        });
        allFollowers = allFollowers.concat(res.data.data);
        cursor = res.data.pagination?.cursor || null;
        keepGoing = !!cursor;
      } catch (error) {
        console.error('Error fetching followers:', error);
        break;
      }
    }
    return allFollowers;
  };

  // Fetch chatters (recent chat users)
  const fetchChatters = async (accessToken, broadcasterId) => {
    try {
      const url = `https://api.twitch.tv/helix/chat/chatters?broadcaster_id=${broadcasterId}&moderator_id=${broadcasterId}`;
      const res = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Client-ID': CLIENT_ID,
        },
      });
      return res.data.data;
    } catch (error) {
      console.error('Error fetching chatters:', error);
      return [];
    }
  };

  // Handle the main sync button
  const handleSyncAll = async () => {
    try {
      if (syncButtonDisabled) {
        toast.warning('Please login and then retry.', { className: 'custom-toast' });
        return;
      }
      setLoading(true);
      setUsersSent(false);

      // 1. Fetch stream info
      const streamInfo = await fetchStreamInfo(user.accessToken, user.id);
      setStreamLive(!!streamInfo);
      console.log('Stream is', streamInfo ? 'LIVE' : 'OFFLINE');

      // 2. Fetch all followers
      const allFollowers = await fetchAllFollowers(user.accessToken, user.id);
      setFollowers(allFollowers);
      console.log('Total Followers:', allFollowers.length);

      // 3. Fetch chatters
      const chattersList = await fetchChatters(user.accessToken, user.id);
      setChatters(chattersList);
      console.log('Total Chatters:', chattersList.length);

      // 4. Log recent chats (from state, live updating)
      console.log('Recent Chats:', recentChats.slice(0, 9));

      // 5. Check if selected user chatted (in recentChats)
      if (electedUser) {
        const displayName = electedUser.user_name || electedUser.from_name || electedUser.from_login || '';
        const userChatted = recentChats.some(
          msg => msg.username.toLowerCase() === displayName.toLowerCase()
        );
        console.log(
          `Did selected user (${displayName}) chat?`,
          userChatted ? 'YES' : 'NO'
        );
      }
    } catch (error) {
      toast.error('Failed to sync data', { className: 'custom-toast' });
      console.error('Sync error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Live chat message tracking via Twitch IRC WebSocket
  useEffect(() => {
    if (!isAuthenticated || !user?.accessToken || !user?.name) return;

    let socket;
    let isMounted = true;
    const channelName = user.name.toLowerCase();

    socket = new window.WebSocket('wss://irc-ws.chat.twitch.tv:443');

    // Handle connection open
    socket.addEventListener('open', () => {
      socket.send(`PASS oauth:${user.accessToken}`);
      socket.send(`NICK ${user.name}`); // Use authenticated user's login as NICK
      socket.send(`JOIN #${channelName}`);
      socket.send('CAP REQ :twitch.tv/membership twitch.tv/tags twitch.tv/commands');
    });

    // Handle incoming messages
    socket.addEventListener('message', (event) => {
      const message = event.data.trim();

      // Respond to PING to keep connection alive
      if (message.startsWith('PING')) {
        socket.send('PONG :tmi.twitch.tv');
        return;
      }

      // Parse IRC PRIVMSG for username and message content
      const match = message.match(/^:([^!]+)!.* PRIVMSG #[^ ]+ :(.*)$/);
      if (match && isMounted) {
        const username = match[1];
        const content = match[2];
        setRecentChats(prev => {
          const updated = [{ username, content }, ...prev];
          // Log live updates to the console
          console.log('Recent Chats:', updated.slice(0, 9));
          return updated.slice(0, 9); // Keep only the 9 most recent
        });
      }
    });

    // === Error handling with toast for live chat only ===
    socket.addEventListener('error', (error) => {
      toast.error('Live chat connection error. Please check your network or login again.', { className: 'custom-toast' });
      console.error('WebSocket error:', error);
    });
    socket.addEventListener('close', (event) => {
      if (event.code !== 1000) {
        toast.error('Live chat connection closed unexpectedly.', { className: 'custom-toast' });
        console.warn('WebSocket closed unexpectedly:', event.reason);
      }
    });

    return () => {
      isMounted = false;
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.close(1000, 'Component unmounted');
      }
    };
  }, [isAuthenticated, user?.accessToken, user?.name]);

  const syncButtonClickSound = () => {
    const audio_sync_click = new Audio('sound/button_sound/Sync_Foll_BTN.ogg');
    audio_sync_click.volume = volume / 100;
    audio_sync_click.play();
  };

  return (
    <div className="followers-panel">
      <div className="sync-group">
        <button
          className="common-but sync-but"
          onClick={() => {
            handleSyncAll();
            if (isSoundOn) syncButtonClickSound();
          }}
          disabled={syncButtonDisabled || loading}
        >
          {loading ? 'Syncing...' : 'Sync All'}
        </button>
        <button id="sync-info" className="sync-info-but" tabIndex={-1}>
          <FaQuestion />
        </button>
        {loading && <img className="sync-loading" src="/images/loading.gif" alt="Loading..." />}
      </div>
      <NameList users={followers} electedUser={electedUser} />
      <Tooltip
        anchorSelect="#sync-info"
        place="right-start"
        className="sync-info-tooltip"
        border="2px solid #f5f5f4"
        style={{ color: "#222", borderRadius: "7px", fontSize: "1.2vw" }}
      >
        <b>How it works:</b><br />
        - Click "Sync All" to fetch and log stream status, followers, chatters, recent chats, and winner chat check.<br />
        <b>Debug:</b> Open browser console for logs.
      </Tooltip>
    </div>
  );
};

export default SyncFollowers;
